import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { brand } from '../config/brand';
import { useLocale } from '../lib/use-locale';
import { navLinks, pagePath } from '../config/page-routes';

/**
 * Responsive site header.
 *
 * Mobile:   [Logo] ···························· [☰ burger]
 *           Drawer slides from right with its own [✕] close button.
 *
 * Desktop:  [Logo] [Nav] ···· [Ghost CTA] [Primary CTA] [Lang]
 *           No burger, no drawer.
 */
const SocialIcons = () => (
  <div className="site-header__social" aria-label="Redes sociales">
    {'instagram' in brand.contact && (
      <a
        href={brand.contact.instagram}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      </a>
    )}
    {'facebook' in brand.contact && (
      <a
        href={brand.contact.facebook}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Facebook"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
      </a>
    )}
    {'youtube' in brand.contact && (
      <a
        href={brand.contact.youtube}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="YouTube"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.54C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
        </svg>
      </a>
    )}
  </div>
);

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  const locale = useLocale();
  const { pathname } = useLocation();

  const close = useCallback(() => setMenuOpen(false), []);
  useEffect(() => {
    close();
  }, [pathname, close]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const homePath = pagePath('home', locale);

  const navItems = (
    <>
      {navLinks.map((page) => {
        const path = pagePath(page, locale);
        return (
          <li key={page}>
            <Link
              to={path}
              className={pathname === path ? 'active' : undefined}
              aria-current={pathname === path ? 'page' : undefined}
              onClick={close}
            >
              {t(`nav.${page}`)}
            </Link>
          </li>
        );
      })}
      <li>
        <Link to={`${homePath}#services`} onClick={close}>
          {t('nav.services')}
        </Link>
      </li>
      <li>
        <Link to={`${homePath}#contact`} onClick={close}>
          {t('nav.contact')}
        </Link>
      </li>
    </>
  );

  return (
    <>
      {/* ===== Header bar ===== */}
      <header className="site-header">
        <div className="container site-header__bar">
          <Link to="/" aria-label={t('nav.home')} className="site-header__logo" onClick={close}>
            <Logo height={36} />
          </Link>

          {/* Desktop only: nav + CTAs + lang */}
          <nav className="site-header__nav" aria-label={t('nav.main')}>
            <ul>{navItems}</ul>
          </nav>

          <div className="site-header__desktop">
            <ThemeToggle />
            {'customerPortalUrl' in brand && (
              <a
                href={brand.customerPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--ghost btn--sm"
              >
                {t('nav.customerPortal')}
              </a>
            )}
            <LanguageSwitcher />
          </div>

          {/* Mobile only: burger */}
          <button
            className="site-header__burger"
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={t('nav.openMenu')}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </header>

      {/* ===== Mobile drawer (outside header) ===== */}
      {menuOpen && <div className="mobile-overlay" onClick={close} aria-hidden="true" />}
      <nav
        id="mobile-menu"
        className={`mobile-drawer ${menuOpen ? 'mobile-drawer--open' : ''}`}
        aria-label={t('nav.main')}
      >
        {/* Close button at the top of the drawer */}
        <button className="mobile-drawer__close" onClick={close} aria-label={t('nav.closeMenu')}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <ul className="mobile-drawer__links">{navItems}</ul>

        <div className="mobile-drawer__ctas">
          {'customerPortalUrl' in brand && (
            <a
              href={brand.customerPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--ghost"
              onClick={close}
            >
              {t('nav.customerPortal')}
            </a>
          )}
        </div>

        <div className="mobile-drawer__lang">
          <SocialIcons />
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </nav>
    </>
  );
}
